// ═══════════════════════════════════════════════════════════════
// Network.jsx — verticalstack XC-NET Network / Trustack surface
// Path: src/components/Network.jsx
//
// S8.B (June 29, 2026) — NEW FILE. Fourth unlocked Cross-cutting
// platform component (after XC-DOC S5, XC-MSG S6, XC-TEAM S7.A).
// S8.C (June 29, 2026) — EDIT. Wire vendor profile detail. Adds
// selectedVendor state + VendorProfilePanel import; the row "View"
// action now opens the profile inline (early-return) and the panel's
// onBack clears it back to the roster table. No PlaceholderDashboard
// change — Network owns its own detail view. View is now always active
// (the S8.B stub styling is removed). onViewProfile prop is retained as
// an OPTIONAL external override (e.g. a future deep-link/route handler);
// when absent, the internal selectedVendor path runs.
//
// WHAT IT IS
//   The relationship surface — "Who is in MY ecosystem?" The persistent
//   vendor roster (CRM-for-vendors), distinct from XC-MKT (transactional
//   hire-now) and XC-TEAM (entity-bound role assignment). Per Strategy
//   §3f LOCKED boundary: Network = WHO EXISTS · Marketplace = HIRE NOW ·
//   Team = WHO'S ASSIGNED.
//
// DATA READ
//   Reads the top-level /vendors collection (platform-wide — memory #4:
//   reputation is platform-wide; no workspaceId scoping). Rules v3.4:
//   read = any signed-in member, write = superAdmin. Canonical §6 vendor
//   shape locked at S8.A: id · name · tier (T1|T2|T3) · trades[] · area ·
//   insurance(bool) · w9OnFile(bool) · paymentTerms · onboardingState ·
//   rating/jobs/response/firstFix(computed) · stripe · contact{name,phone}.
//
//   Client-side filter + sort (getDocs, not onSnapshot) — at current
//   roster volume no composite index is needed. The vendors tier+rating
//   index (CICAgNiryokK) exists for future server-side queries (S8.D+).
//
// AS-S8-NET-NOT-STAGE-SCOPED (LOW reverse)
//   The sandbox xcNetwork view filters vendors by current stage because
//   it renders inside a stage module. The real Cross-cutting nav has NO
//   current-stage context (same as Documents/Messages/Team shipped
//   standalone). So Network shows the FULL platform-wide roster, filtered
//   by tier + category, not stage. Stage-scoped filtering defers until
//   Cross-cutting components gain stage-context awareness (sibling of the
//   RevPlan parcel-context deferral). Category filter maps against the
//   canonical trades[] field (sandbox demo used stages[]).
//
// FILTER
//   <XcFilterBar viewId="xc-network"> — registered in xcFilterBar.js
//   (Tier: All/T1/T2/T3 · Category: All/Trades/Legal/Finance/Brokerage/
//   Other). onChange drives client-side filtering. No edit to xcFilterBar.
//
// PROPS
//   workspace    — current workspace (accepted for consistency / future
//                  relationship scoping; reads are platform-wide)
//   roles        — caller roles (reserved; roster is readable by all)
//   roleLabel    — caller role label (reserved)
//   onViewProfile(vendor) — OPTIONAL external override for the "View"
//                  action. When provided, it is called instead of the
//                  internal panel (for a future deep-link/route handler).
//                  When absent, View opens VendorProfilePanel inline.
//   mockMode     — when true, renders a hardcoded multi-tier vendor array
//                  for the ?xcnet-smoke=1 harness (no Firestore read).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import XcFilterBar from './xcFilterBar';
import VendorProfilePanel from './VendorProfilePanel';
import { matchVendors } from './matchVendors';

// ── Tier presentation (per sandbox v7.2 xcNetwork) ───────────────────────
const TIER_META = {
  T1: { label: 'T1', descriptor: 'verified',  color: '#0A5C42', bg: '#E8F7F2', sub: 'prior relationship + transactions' },
  T2: { label: 'T2', descriptor: 'adjacent',  color: '#7A4A08', bg: '#FDF3E3', sub: 'community-vouched' },
  T3: { label: 'T3', descriptor: 'open pool', color: '#5A6478', bg: '#F0F2F5', sub: 'Google Places + cold invites' },
};
const TIER_ORDER = { T1: 0, T2: 1, T3: 2 };

// ── Trade → coarse category mapping (xc-network filter buckets) ──────────
// The xc-network filter Category options are coarse (Trades / Legal /
// Finance / Brokerage / Other). Map each vendor's trades[] to one bucket
// by keyword. Best-effort grouping; refine if the taxonomy formalizes
// vendor categories in a later session.
const CATEGORY_KEYWORDS = {
  Legal:     ['legal', 'attorney', 'counsel', 'law', '1031', 'qi', 'title', 'closing'],
  Finance:   ['lender', 'loan', 'bank', 'cpa', 'tax', 'account', 'bookkeep', 'syndicat', 'draw'],
  Brokerage: ['broker', 'brokerage', 'realtor', 'land broker', 'commercial broker', 'forester', 'appraiser'],
  Trades:    ['maintenance', 'hvac', 'plumbing', 'electrical', 'roof', 'pool', 'spa', 'pest',
              'landscap', 'paint', 'gc', 'general', 'construction', 'steel', 'restoration',
              'mep', 'property_management', 'turnover', 'repair', 'gates', 'fence'],
};

function categoryOf(vendor) {
  const trades = (vendor.trades || []).map((t) => String(t).toLowerCase());
  const hay = trades.join(' ');
  for (const cat of ['Legal', 'Finance', 'Brokerage', 'Trades']) {
    if (CATEGORY_KEYWORDS[cat].some((kw) => hay.includes(kw))) return cat;
  }
  return 'Other';
}

function prettyTrades(trades) {
  if (!trades || trades.length === 0) return '—';
  return trades
    .map((t) => String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' · ');
}

// ── Mock roster for ?xcnet-smoke=1 harness ───────────────────────────────
const MOCK_VENDORS = [
  { id: 'vendor-aps-mgmt', name: 'APS Management', tier: 'T1', trades: ['property_management', 'general_maintenance', 'turnover'], area: 'Canton, OH', insurance: true, w9OnFile: false, onboardingState: 'active', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: 'Larry Brown', phone: '330-327-7742' } },
  { id: 'vendor-everguard', name: 'EverGuard LLC', tier: 'T1', trades: ['hvac', 'plumbing', 'general_repair'], area: 'Marietta, OH', insurance: true, w9OnFile: true, onboardingState: 'active', rating: 4.8, jobs: 23, response: 2, firstFix: 88, stripe: 'acct_mock_eg', paymentTerms: 'Net 15', contact: { name: 'Seth', phone: '—' }, signals: { external: { bbb: 4.5, google: 4.9, yelp: 4.7 }, responsiveness: { avgResponseHours: 2 }, completion: { completionRate: 95, firstFixRate: 88 }, recency: { lastActivityAt: Date.now() - 10 * 86400000 } } },
  { id: 'vendor-sikora', name: 'Sikora Law LLC', tier: 'T1', trades: ['legal', '1031_qi', 'closing_attorney'], area: 'Columbus, OH', insurance: true, w9OnFile: true, onboardingState: 'active', rating: 5.0, jobs: 4, response: 6, firstFix: 100, stripe: null, paymentTerms: 'Net 30', contact: { name: 'Mike Sikora', phone: '—' }, signals: { external: { google: 5.0, yelp: 5.0 }, responsiveness: { avgResponseHours: 6 }, completion: { completionRate: 100, firstFixRate: 100 }, recency: { lastActivityAt: Date.now() - 30 * 86400000 } } },
  { id: 'vendor-nai', name: 'NAI Global', tier: 'T1', trades: ['commercial_broker'], area: 'Kansas City, MO', insurance: true, w9OnFile: false, onboardingState: 'active', rating: 4.6, jobs: 2, response: 12, firstFix: null, stripe: null, paymentTerms: null, contact: { name: 'Dan Spring', phone: '—' }, signals: { external: { google: 4.6 }, recency: { lastActivityAt: Date.now() - 120 * 86400000 } } },
  { id: 'vendor-stark', name: 'Stark Electric', tier: 'T2', trades: ['electrical'], area: 'Canton, OH', insurance: true, w9OnFile: false, onboardingState: 'onboarding', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '(local)', phone: '—' } },
  { id: 'vendor-tristate', name: 'TriState Mechanical', tier: 'T2', trades: ['hvac', 'mep'], area: 'Canton, OH', insurance: false, w9OnFile: false, onboardingState: 'invited', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '(local)', phone: '—' } },
  { id: 'vendor-va-forester', name: 'TBD-VA Forester', tier: 'T3', trades: ['forester_timber'], area: 'Montgomery Co, VA', insurance: false, w9OnFile: false, onboardingState: 'invited', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '—', phone: '—' } },
  { id: 'vendor-ar-land', name: 'TBD-AR Land Broker', tier: 'T3', trades: ['land_broker'], area: 'Cherokee Village, AR', insurance: false, w9OnFile: false, onboardingState: 'invited', rating: null, jobs: 0, response: null, firstFix: null, stripe: null, paymentTerms: null, contact: { name: '—', phone: '—' } },
];

// ── Component ────────────────────────────────────────────────────────────
export default function Network({ workspace, roles, roleLabel, onViewProfile, mockMode = false }) {
  const [vendors, setVendors] = useState(mockMode ? MOCK_VENDORS : []);
  const [loading, setLoading] = useState(!mockMode);
  const [error, setError] = useState(null);
  const [filterValues, setFilterValues] = useState({ tier: 'All', category: 'All' });
  const [selectedVendor, setSelectedVendor] = useState(null);

  // ── S8.F: client-side vendor recommendation demo ───────────────────────
  const [woCategory, setWoCategory] = useState('');
  const [woArea, setWoArea] = useState('');
  const [suggestions, setSuggestions] = useState(null);

  useEffect(() => {
    if (mockMode) return;
    let cancelled = false;

    async function loadVendors() {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(collection(db, 'vendors'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setVendors(list);
      } catch (err) {
        if (cancelled) return;
        console.error('[Network] vendors load error:', err);
        setError(err.message || 'Failed to load vendors');
        setVendors([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadVendors();
    return () => { cancelled = true; };
  }, [mockMode]);

  // ── S8.C: vendor profile detail (early return) ─────────────────────────
  // When a vendor is selected (row "View"), render the detail panel in
  // place of the roster. onBack clears selection → back to the table.
  if (selectedVendor) {
    return (
      <VendorProfilePanel
        vendor={selectedVendor}
        onBack={() => setSelectedVendor(null)}
      />
    );
  }

  // ── View action handler ────────────────────────────────────────────────
  // External override (onViewProfile) wins if provided; otherwise open the
  // internal VendorProfilePanel via selectedVendor.
  const handleView = (v) => {
    if (onViewProfile) onViewProfile(v);
    else setSelectedVendor(v);
  };

  // ── Filter + sort (client-side) ────────────────────────────────────────
  const filtered = vendors
    .filter((v) => {
      const tier = v.tier || 'T3';
      if (filterValues.tier !== 'All' && tier !== filterValues.tier) return false;
      if (filterValues.category !== 'All' && categoryOf(v) !== filterValues.category) return false;
      return true;
    })
    .sort((a, b) => {
      const ta = TIER_ORDER[a.tier] ?? 9;
      const tb = TIER_ORDER[b.tier] ?? 9;
      if (ta !== tb) return ta - tb;
      return (a.name || '').localeCompare(b.name || '');
    });

  // ── KPI counts (across full roster, pre-filter) ────────────────────────
  const count = (t) => vendors.filter((v) => (v.tier || 'T3') === t).length;
  const kpis = [
    { label: 'T1 · verified',  value: count('T1'), sub: 'prior relationship + transactions', accent: true },
    { label: 'T2 · adjacent',  value: count('T2'), sub: 'community-vouched' },
    { label: 'T3 · open pool', value: count('T3'), sub: 'Google Places + cold invites' },
    { label: 'Total in network', value: vendors.length, sub: 'all tiers' },
  ];

  // ── S8.F: derive job-category options from the live roster's trades ─────
  const categoryOptions = Array.from(
    new Set(vendors.flatMap((v) => v.trades || []))
  )
    .map((t) => ({ value: t, label: String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const runMatch = () => {
    if (!woCategory) { setSuggestions([]); return; }
    const ranked = matchVendors(
      { jobCategory: woCategory, serviceArea: woArea },
      vendors,
      { topN: 3 }
    );
    setSuggestions(ranked);
  };

  return (
    <div style={styles.container}>
      {/* ── Banner ────────────────────────────────────────────────── */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Network / Trustack</span>
          <span style={styles.bannerTag}>XC-NET</span>
        </div>
        <div style={styles.bannerMuted}>
          Relationship surface · "Who is in MY ecosystem?" · vendor roster · 3-tier rating · onboarding pipeline
        </div>
      </div>

      {/* ── Page head ─────────────────────────────────────────────── */}
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.pageTitle}>Network</h1>
          <div style={styles.pageSub}>
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} in your ecosystem
          </div>
        </div>
        <div style={styles.pageActions}>
          <button type="button" style={styles.ghostBtn} disabled title="Onboarding pipeline — Tier 3">
            ⎕ Onboarding pipeline
          </button>
          <button type="button" style={styles.ghostBtn} disabled title="Add vendor — wires with vendor self-onboard (S9.D)">
            + Add vendor
          </button>
        </div>
      </div>

      {/* ── XC-NET vs XC-MKT distinction note ─────────────────────── */}
      <div style={styles.noteCard}>
        <b>XC-NET vs XC-MKT:</b> Network is the persistent vendor roster (CRM-for-vendors).
        Marketplace (S9) is the transactional search — "find for THIS job" — and queries this
        roster for candidates. Same roster, two questions: who exists vs. hire now.
      </div>

      {/* ── KPI grid ──────────────────────────────────────────────── */}
      <div style={styles.kpiGrid}>
        {kpis.map((k) => (
          <div key={k.label} style={{ ...styles.kpiCard, ...(k.accent ? styles.kpiCardAccent : {}) }}>
            <div style={styles.kpiLabel}>{k.label}</div>
            <div style={styles.kpiValue}>{k.value}</div>
            <div style={styles.kpiSub}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar (xc-network viewId, registered) ────────────── */}
      <XcFilterBar viewId="xc-network" onChange={setFilterValues} />

      {/* ── Trustack rating signals card ──────────────────────────── */}
      <div style={styles.signalsCard}>
        <div style={styles.cardHeader}>Trustack rating signals</div>
        <div style={styles.signalsBody}>
          <div><b>External:</b> BBB · Google · Yelp aggregation</div>
          <div><b>Responsiveness:</b> Twilio response time</div>
          <div><b>Transactional:</b> job completion rate via Marketplace fires</div>
          <div><b>Decay:</b> 90 days no activity → T1 → T1 Inactive</div>
          <div><b>Insurance:</b> AI-08 OCR on W-9 + COI status</div>
        </div>
        <div style={styles.signalsFootnote}>
          Weighted blend computed per vendor — algorithm v1 active (S8.D).
        </div>
      </div>

      {/* ── Suggested vendors (S8.F · client-side matcher) ──────────── */}
      <div style={styles.suggestCard}>
        <div style={styles.cardHeader}>Suggested vendors</div>
        <div style={styles.suggestIntro}>
          Pick a job category and area — the roster is ranked by trade overlap,
          location, and Trustack score. This is the local matcher; the AI-06
          dispatch model (matchSubcontractors) wires job-attached in Marketplace (S9).
        </div>

        <div style={styles.suggestForm}>
          <select
            value={woCategory}
            onChange={(e) => setWoCategory(e.target.value)}
            style={styles.suggestSelect}
          >
            <option value="">Job category…</option>
            {categoryOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={woArea}
            onChange={(e) => setWoArea(e.target.value)}
            placeholder="Service area (e.g. Canton, OH) — optional"
            style={styles.suggestInput}
          />
          <button
            type="button"
            style={woCategory ? styles.suggestBtn : styles.suggestBtnDisabled}
            onClick={runMatch}
            disabled={!woCategory}
          >
            Suggest
          </button>
        </div>

        {suggestions !== null && (
          suggestions.length === 0 ? (
            <div style={styles.suggestEmpty}>
              No vendors with a matching trade for that category.
            </div>
          ) : (
            <div style={styles.suggestResults}>
              {suggestions.map((s, i) => {
                const tm = TIER_META[s.vendor.tier] || TIER_META.T3;
                return (
                  <div key={s.vendor.id} style={styles.suggestRow}>
                    <span style={styles.suggestRank}>#{i + 1}</span>
                    <span style={{ ...styles.tierBadge, color: tm.color, background: tm.bg }}>
                      {tm.label}
                    </span>
                    <div style={styles.suggestMain}>
                      <div style={styles.suggestName}>{s.vendor.name}</div>
                      <div style={styles.suggestReasons}>
                        {s.vendor.area || '—'} · {s.reasons.join(' · ')}
                      </div>
                    </div>
                    <span style={styles.suggestScore}>{s.score}</span>
                    <button
                      type="button"
                      style={styles.suggestViewBtn}
                      onClick={() => handleView(s.vendor)}
                    >
                      View
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* ── Vendor table ──────────────────────────────────────────── */}
      <div style={styles.tableCard}>
        <div style={styles.cardHeader}>
          Vendors {filterValues.tier !== 'All' || filterValues.category !== 'All' ? '(filtered)' : ''}
        </div>

        {error ? (
          <div style={styles.errorState}>Could not load vendors: {error}</div>
        ) : loading ? (
          <div style={styles.loadingState}>Loading network…</div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            {vendors.length === 0
              ? 'No vendors in the network yet. Seeding the T1 pool lands at S8.E.'
              : 'No vendors match the current tier / category filter.'}
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Vendor</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Trades</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.thRight}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const tm = TIER_META[v.tier] || TIER_META.T3;
                  return (
                    <tr key={v.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={{ ...styles.tierBadge, color: tm.color, background: tm.bg }}>
                          {tm.label}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.vendorName}>{v.name || 'Unnamed vendor'}</div>
                        {v.area && <div style={styles.vendorArea}>{v.area}</div>}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.contactName}>{v.contact?.name || '—'}</div>
                        {v.contact?.phone && v.contact.phone !== '—' && (
                          <div style={styles.contactPhone}>{v.contact.phone}</div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.tradesText}>{prettyTrades(v.trades)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.statusPill}>{v.onboardingState || 'active'}</span>
                        <div style={styles.statusBadges}>
                          {v.insurance ? <span style={styles.okBadge}>✓ COI</span> : <span style={styles.gapBadge}>no COI</span>}
                          {v.w9OnFile ? <span style={styles.okBadge}>✓ W-9</span> : <span style={styles.gapBadge}>no W-9</span>}
                        </div>
                      </td>
                      <td style={styles.tdRight}>
                        <button
                          type="button"
                          style={styles.viewBtn}
                          onClick={() => handleView(v)}
                          title="View vendor profile"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline styles (migration-epoch idiom: Arial, navy/cyan, inline) ──────
const NAVY = '#003060';
const CYAN = '#00B8D0';
const styles = {
  container: { maxWidth: 1100, margin: '0 auto', fontFamily: 'Arial, sans-serif', color: '#1F2937' },
  banner: {
    backgroundColor: '#F0F7FA', border: `1px solid ${CYAN}33`, borderRadius: 8,
    padding: '12px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: 8,
  },
  bannerTitle: { fontSize: 14, fontWeight: 700, color: NAVY },
  bannerTag: {
    display: 'inline-block', marginLeft: 8, padding: '2px 8px', fontSize: 10, fontWeight: 700,
    color: CYAN, background: `${CYAN}1F`, borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  bannerMuted: { fontSize: 12, color: '#6B7280' },
  pageHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16, flexWrap: 'wrap', gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 700, color: NAVY, margin: 0 },
  pageSub: { fontSize: 13, color: '#6B7280', marginTop: 4 },
  pageActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  ghostBtn: {
    background: '#FFFFFF', border: '1px solid #D0D5DD', color: '#9CA3AF',
    padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'not-allowed', fontFamily: 'inherit',
  },
  noteCard: {
    background: '#F8FAFC', border: '1px solid #E5E8EE', borderRadius: 8,
    padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#374151', lineHeight: 1.55,
  },
  kpiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14, marginBottom: 16,
  },
  kpiCard: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16,
  },
  kpiCardAccent: { border: `1px solid ${CYAN}`, boxShadow: '0 2px 8px rgba(0,184,208,0.10)' },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  kpiValue: { fontSize: 28, fontWeight: 700, color: NAVY, margin: '6px 0 2px' },
  kpiSub: { fontSize: 11, color: '#9CA3AF' },
  signalsCard: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, marginBottom: 16,
  },
  cardHeader: {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#9CA3AF',
    textTransform: 'uppercase', marginBottom: 12,
  },
  signalsBody: { fontSize: 12, color: '#374151', lineHeight: 1.8, display: 'grid', gap: 2 },
  signalsFootnote: { marginTop: 10, fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' },
  tableCard: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, marginBottom: 20,
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: 0.5, padding: '8px 10px', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap',
  },
  thRight: {
    textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: 0.5, padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
  },
  tr: { borderBottom: '1px solid #F1F5F9' },
  td: { padding: '10px', verticalAlign: 'top' },
  tdRight: { padding: '10px', textAlign: 'right', verticalAlign: 'top' },
  tierBadge: {
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
    fontFamily: 'Consolas, monospace',
  },
  vendorName: { fontSize: 13, fontWeight: 700, color: NAVY },
  vendorArea: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },
  contactName: { fontSize: 12, color: '#374151' },
  contactPhone: { fontSize: 11, color: '#9CA3AF', marginTop: 2, fontFamily: 'Consolas, monospace' },
  tradesText: { fontSize: 12, color: '#374151' },
  statusPill: {
    display: 'inline-block', fontSize: 10, fontWeight: 700, color: NAVY, background: '#F0F4F8',
    padding: '2px 8px', borderRadius: 10, textTransform: 'capitalize',
  },
  statusBadges: { display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' },
  okBadge: {
    fontSize: 9, fontWeight: 700, color: '#0A5C42', background: '#E8F7F2',
    padding: '2px 6px', borderRadius: 8,
  },
  gapBadge: {
    fontSize: 9, fontWeight: 700, color: '#9CA3AF', background: '#F3F4F6',
    padding: '2px 6px', borderRadius: 8,
  },
  viewBtn: {
    background: '#FFFFFF', border: `1px solid ${CYAN}`, color: CYAN,
    padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  suggestCard: {
    background: '#FFFFFF', border: `1px solid ${CYAN}55`, borderRadius: 10, padding: 16, marginBottom: 16,
  },
  suggestIntro: { fontSize: 12, color: '#6B7280', lineHeight: 1.55, marginBottom: 14 },
  suggestForm: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 },
  suggestSelect: {
    padding: '8px 10px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', color: '#1F2937', background: '#FFFFFF', minWidth: 180,
  },
  suggestInput: {
    padding: '8px 10px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', color: '#1F2937', flex: 1, minWidth: 200,
  },
  suggestBtn: {
    background: NAVY, border: 'none', color: '#FFFFFF', padding: '9px 20px', borderRadius: 6,
    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  suggestBtnDisabled: {
    background: '#D0D5DD', border: 'none', color: '#FFFFFF', padding: '9px 20px', borderRadius: 6,
    fontSize: 13, fontWeight: 700, cursor: 'not-allowed', fontFamily: 'inherit',
  },
  suggestEmpty: { marginTop: 14, fontSize: 13, color: '#6B7280', fontStyle: 'italic' },
  suggestResults: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  suggestRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
    background: '#F8FAFC', borderRadius: 8, border: '1px solid #EEF1F5',
  },
  suggestRank: { fontSize: 13, fontWeight: 700, color: CYAN, minWidth: 22 },
  suggestMain: { flex: 1, minWidth: 0 },
  suggestName: { fontSize: 13, fontWeight: 700, color: NAVY },
  suggestReasons: { fontSize: 11, color: '#6B7280', marginTop: 2 },
  suggestScore: { fontSize: 18, fontWeight: 700, color: NAVY, minWidth: 32, textAlign: 'right' },
  suggestViewBtn: {
    background: '#FFFFFF', border: `1px solid ${CYAN}`, color: CYAN, padding: '5px 12px',
    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  loadingState: { color: '#9CA3AF', fontStyle: 'italic', fontSize: 13, padding: '16px 4px' },
  errorState: {
    color: '#B91C1C', fontSize: 13, padding: '12px 16px', backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5', borderRadius: 6,
  },
  emptyState: {
    color: '#6B7280', fontSize: 13, fontStyle: 'italic', padding: '20px 4px',
  },
};
